import React, { ReactNode } from 'react';
import { useLocation } from 'react-router';
import styled, { css } from 'styled-components';

import Link from '#/components/basic/Link/Link';
import LinkClickable from '#/components/basic/Link/LinkClickable';
import RouterNavLink from '#/components/basic/Link/RouterNavLink';
import { menuItemPadding } from '#/components/layout/header/commonStyles';
import { isDashboardPath } from '#/components/trading/dashboardDetect';

const RouteLinkWrap = styled.div`
  /* position: relative; */
  /* width: 100%; */
  display: flex;
  /* align-items: center; */
`;

const dropdownMenuLinkStyles = css`
  width: 100%;
  transition: background-color 0.2s ease;
  padding: 0 22px;
  &:hover {
    background: var(--primary7);
  }
  &:active {
    background: var(--gray2);
  }
`;

type LinkStyleProps = {
  $isDropdownItem: boolean;
  $isTopMenu: boolean;
  $paddingRight: string | undefined;
};

const linkStyles = css<LinkStyleProps>`
  && {
    display: flex;
    align-items: center;
    height: ${({ $isTopMenu }) => ($isTopMenu ? 'initial' : '32px')};
    ${props =>
      props.$isDropdownItem ? dropdownMenuLinkStyles : menuItemPadding}
    ${({ $paddingRight }) =>
      $paddingRight ? `padding-right: ${$paddingRight};` : null}
    color: ${({ $isTopMenu }) =>
      $isTopMenu ? 'var(--gray4d)' : 'var(--white)'};
  }
  &&:hover,
  &&.active {
    color: var(--white);
  }
`;

const Centered = styled.div`
  display: inline-flex;
  align-items: center;
`;

const ImgWrap = styled.div<{ $iconSize?: string }>`
  position: absolute;
  display: flex;
  align-items: center;
  > img,
  > svg {
    opacity: 0.5;
    max-height: ${({ $iconSize }) => $iconSize || '16px'};
    max-width: ${({ $iconSize }) => $iconSize || '17px'};
  }
`;

const RouteLink = styled(RouterNavLink)<LinkStyleProps>`
  ${linkStyles}
`;

const NormalLink = styled(Link)<LinkStyleProps>`
  ${linkStyles}
`;

const ClickableLink = styled(LinkClickable)<LinkStyleProps>`
  ${linkStyles}
`;

type MenuLinkProps = {
  title: React.ReactNode;
  to?: string;
  linkWindowTarget?: '_self' | '_blank';
  isActive?: boolean;
  isTradingIndex?: boolean;
  isTopMenu: boolean;
  isDropdownItem?: boolean;
  onClick?: () => void;
  isDropdownOpen?: boolean;
  setDropdownOpen?: (isOpen: boolean) => void;
  allowReferrer?: boolean;
  icon?: ReactNode;
  iconSize?: string;
  className?: string;
  paddingRight?: string;
};

export default function MenuLink({
  title,
  to,
  linkWindowTarget,
  isActive = undefined,
  isTradingIndex = false,
  isDropdownItem = false,
  isTopMenu = false,
  onClick,
  allowReferrer,
  icon,
  iconSize,
  className,
  paddingRight,
}: MenuLinkProps) {
  const location = useLocation();
  const linkTitle = isTopMenu ? (
    <div>{title}</div>
  ) : icon ? (
    <Centered>
      <ImgWrap $iconSize={iconSize}>{icon}</ImgWrap>
      <div style={{ width: 33 }} />
      {title}
    </Centered>
  ) : (
    title
  );
  return (
    <RouteLinkWrap className={className}>
      {onClick && !to ? (
        <ClickableLink
          onClick={onClick}
          $isDropdownItem={isDropdownItem}
          $isTopMenu={isTopMenu}
          $paddingRight={paddingRight}
        >
          {linkTitle}
        </ClickableLink>
      ) : to?.startsWith('http') ? (
        <NormalLink
          href={to ?? '#'}
          target={linkWindowTarget}
          onClick={onClick}
          $isDropdownItem={isDropdownItem}
          $isTopMenu={isTopMenu}
          allowReferrer={allowReferrer}
          $paddingRight={paddingRight}
        >
          {linkTitle}
        </NormalLink>
      ) : (
        <RouteLink
          $isDropdownItem={isDropdownItem}
          $isTopMenu={isTopMenu}
          to={to}
          $paddingRight={paddingRight}
          isActive={
            typeof isActive === 'boolean'
              ? isActive
              : isTradingIndex
              ? isDashboardPath(location?.pathname || '')
              : !to // disabled state when undefined or empty
              ? false
              : undefined
          }
        >
          {linkTitle}
        </RouteLink>
      )}
    </RouteLinkWrap>
  );
}
